import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './Header.css';
import logo from '../assets/logo.png'; // This path will need to match where you place your logo
import { logoutUser } from '../services/api';

function Header() {
  const [cartCount, setCartCount] = useState(0);
  const [showNotification, setShowNotification] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if user is logged in and listen for storage changes
  useEffect(() => {
    const checkLoginStatus = () => {
      const token = localStorage.getItem('token');
      const oldAuthToken = localStorage.getItem('authToken'); // Check for old token name too
      setIsLoggedIn(!!(token || oldAuthToken));
      
      console.log('Login status checked:', !!token || !!oldAuthToken);
    };
    
    // Initial check
    checkLoginStatus();
    
    // Listen for storage changes (in case of logout in another tab)
    window.addEventListener('storage', checkLoginStatus);
    
    return () => {
      window.removeEventListener('storage', checkLoginStatus);
    };
  }, []);
  
  // Monitor cart changes
  useEffect(() => {
    // Get cart from localStorage
    const updateCartCount = () => {
      const cartData = localStorage.getItem('cart');
      if (cartData) {
        const cart = JSON.parse(cartData);
        const newCount = cart.reduce((total, item) => total + item.quantity, 0);
        
        setCartCount(prevCount => {
          // If cart count has increased, show notification
          if (newCount > prevCount && prevCount > 0) {
            setShowNotification(true);
            setTimeout(() => setShowNotification(false), 2000);
          }
          return newCount;
        });
      } else {
        setCartCount(0);
      }
    };
    
    // Initial count
    updateCartCount();
    
    // Set up event listener for localStorage changes
    window.addEventListener('storage', updateCartCount);
    
    // Custom event for cart updates (will be triggered from other components)
    const handleCartUpdate = () => {
      console.log('Cart updated event received');
      updateCartCount();
    };
    window.addEventListener('cartUpdated', handleCartUpdate);
    
    return () => {
      window.removeEventListener('storage', updateCartCount);
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, []);

  // Handle logout
  const handleLogout = async () => {
    if (isLoading) return;
    
    console.log('Logout button clicked');
    setIsLoading(true);
    try {
      // Also handle old token format for backward compatibility
      if (localStorage.getItem('authToken')) {
        localStorage.removeItem('authToken');
      }
      
      await logoutUser(true);
      setIsLoggedIn(false);
      navigate('/');
      console.log('Logout successful');
    } catch (error) {
      console.error('Error logging out:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <header className="header">
      <div className="logo">
        <Link to="/">
          <img src={logo} alt="Revive Wellness Center Logo" className="logo-image" />
        </Link>
      </div>
      
      {/* Desktop Navigation */}
      <nav className="nav desktop-nav">
        <ul>
          <li><Link to="/menu">Menu</Link></li>
          <li className="cart-link">
            <Link to="/cart">
              Cart
              {cartCount > 0 && (
                <span className={`cart-badge ${showNotification ? 'pulse' : ''}`}>
                  {cartCount}
                </span>
              )}
            </Link>
          </li>
          <li><Link to="/about">About Us</Link></li>
          <li><Link to="/contact">Contact</Link></li>
          {isLoggedIn && location.pathname === '/admin' && (
            <li>
              <a 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  handleLogout();
                }}
                className={isLoading ? 'disabled-link' : ''}
                aria-label="Logout from your account"
              >
                {isLoading ? 'Logging out...' : 'Logout'}
              </a>
            </li>
          )}
        </ul>
      </nav>
      
      {/* Mobile Cart Link and Auth */}
      <div className="mobile-actions">
        <Link to="/cart" className="cart-link-mobile">
          Cart
          {cartCount > 0 && (
            <span className={`cart-badge ${showNotification ? 'pulse' : ''}`}>
              {cartCount}
            </span>
          )}
        </Link>
        
        {isLoggedIn && location.pathname === '/admin' && (
          <div className="mobile-auth">
            <a 
              href="#" 
              onClick={(e) => {
                e.preventDefault();
                handleLogout();
              }}
              className={`mobile-admin ${isLoading ? 'disabled-link' : ''}`}
              aria-label="Logout from your account"
            >
              {isLoading ? '...' : 'Logout'}
            </a>
          </div>
        )}
      </div>
    </header>
  );
}

export default Header;